<section class="context" aria-label="Current agent context">
  <span class="context-kind">{{ workspaces.active().tabType || 'Application' }}</span>
  <strong>{{ workspaces.active().label }}</strong>
  @if (workspaces.active().dataServiceId) { <small>Data Service: {{ workspaces.active().dataServiceId }}</small> }
  @if (workspaces.active().modelName) { <small>Model: {{ workspaces.active().modelName }}</small> }
  @if (workspaces.active().viewName) { <small>View: {{ workspaces.active().viewName }}</small> }
</section>
<div #transcript class="transcript" role="log" aria-label="Conversation" aria-live="polite" (scroll)="onScroll()">
  @if (!session()?.messages?.length) {
    <div class="empty"><span class="material-symbols-outlined" aria-hidden="true">auto_awesome</span>
      <h3>How can I help?</h3><p>This conversation stays with your workspace.</p></div>
  }
  @for (message of session()?.messages; track message.id) {
    @if (message.role === 'user' || message.text) {
      <article class="message" [class.user]="message.role === 'user'" [class.agent]="message.role === 'assistant'">
        <div class="message-heading">
          <span class="message-avatar material-symbols-outlined" aria-hidden="true">{{ message.role === 'user' ? 'person' : 'auto_awesome' }}</span>
          <span class="speaker">{{ message.role === 'user' ? 'You' : 'Agent' }}</span>
        </div>
        @if (message.role === 'assistant') {
          <div class="message-text message-markdown" [innerHTML]="message.text | agentMarkdown"></div>
        } @else {
          <div class="message-text">{{ message.text }}</div>
          @if (message.attachmentNames?.length) {
            <div class="message-attachments" aria-label="Attached references">
              @for (name of message.attachmentNames; track name) { <span><span class="material-symbols-outlined" aria-hidden="true">attach_file</span>{{ name }}</span> }
            </div>
          }
        }
      </article>
    }
  }
  @for (choice of pendingChoices(); track choice.choiceId) {
    <article class="message agent">
      <div class="message-heading"><span class="message-avatar material-symbols-outlined" aria-hidden="true">auto_awesome</span><span class="speaker">Agent</span></div>
      <div class="message-text">{{ choice.message }}</div>
      <div class="agent-choices" aria-label="Available choices">
        @for (option of choice.options; track option.optionId; let index = $index) {
          <button type="button" (click)="choose(choice, option.optionId)"><span>{{ index + 1 }}</span>{{ option.label }}</button>
        }
      </div>
    </article>
  }
  @for (activity of session()?.activities; track activity.id) {
    <div class="activity" [class.completed]="activity.completed">
      <span class="activity-indicator" aria-hidden="true">{{ activity.completed ? '✓' : '' }}</span>
      <span>{{ activity.label }}</span>
    </div>
  }
  @if (session()?.status === 'streaming') {
    <div class="working-status" role="status" aria-label="Agent is working">
      <span class="working-spark material-symbols-outlined" aria-hidden="true">auto_awesome</span>
      <span class="working-label">Working</span>
      <span class="working-dots" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="working-track" aria-hidden="true"><i></i></span>
    </div>
  }
  @if (session()?.status === 'cancelled') { <p class="status">Response stopped.</p> }
  @if (session()?.error) {
    <div class="error" role="alert"><p>{{ session()?.error }}</p><button mat-button (click)="editLastPrompt()">Edit and retry</button></div>
  }
  @if (session()?.diagnostics?.length) {
    <details class="diagnostics">
      <summary>Agent diagnostics · {{ diagnosticBatches().length }} requests · {{ session()?.diagnostics?.length }} events
        @if (totalDuration() !== undefined) { <span> · Total {{ formatDuration(totalDuration()) }}</span> }
      </summary>
      @for (batch of diagnosticBatches(); track batch.requestId; let requestIndex = $index; let isLatest = $last) {
        <details class="diagnostic-batch" [open]="isLatest">
          <summary>Request {{ requestIndex + 1 }} · {{ batch.diagnostics.length }} events
            @if (requestDuration(batch) !== undefined) { <span> · {{ formatDuration(requestDuration(batch)) }}</span> }
          </summary>
          @for (diagnostic of batch.diagnostics; track $index) {
            <article><strong>{{ diagnostic.stage }} · {{ diagnostic.status }}</strong>@if (diagnostic.tool) { <span> · {{ diagnostic.tool }}</span> }@if (diagnosticDuration(diagnostic) !== undefined) { <span> · {{ formatDuration(diagnosticDuration(diagnostic)) }}</span> }
              <pre>{{ diagnosticText(diagnostic.details) }}</pre></article>
          }
        </details>
      }
    </details>
  }
</div>
<form class="composer" (ngSubmit)="send()">
  <label for="agent-prompt" class="visually-hidden">Message to Agent</label>
  <textarea #composer id="agent-prompt" name="prompt" rows="3" maxlength="32000" placeholder="Ask about this workspace…"
    [ngModel]="session()?.draft || ''" (ngModelChange)="setDraft($event)" (keydown)="composerKey($event)"></textarea>
  <div class="attachments" aria-label="Reference attachments">
    @for (attachment of session()?.attachments; track attachment.name + attachment.sizeBytes; let index = $index) {
      <span class="attachment"><span class="material-symbols-outlined" aria-hidden="true">attach_file</span>{{ attachment.name }}
        <button type="button" [attr.aria-label]="'Remove ' + attachment.name" (click)="removeAttachment(index)">×</button></span>
    }
  </div>
  @if (session()?.attachmentError) { <small class="attachment-error" role="alert">{{ session()?.attachmentError }}</small> }
  <div class="composer-actions">
    <label class="attach-button" for="agent-attachments"><span class="material-symbols-outlined" aria-hidden="true">attach_file</span>Attach reference</label>
    <input id="agent-attachments" type="file" multiple accept=".txt,.md,.csv,.json,.pdf,.png,.jpg,.jpeg,.webp" (change)="selectAttachments($event)">
    <small>TXT, PDF, PNG, JPG, WebP · 5 files / 20 MB</small>
    @if (session()?.status === 'streaming') {
      <button mat-stroked-button type="button" (click)="stop()">Stop</button>
    } @else {
      <button mat-flat-button type="submit" [disabled]="!session()?.draft?.trim() || !client.canSend(workspaces.active())">Send</button>
    }
  </div>
  @if (!client.canSend(workspaces.active())) { <small role="status">Chat is not available in this workspace yet.</small> }
</form>
